import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isSupported } from 'twilio-video';
import VideoContext from 'contexts/videoContext';
import { useVideoConnectionOptions, useUserMedia } from 'hooks';
import useHandleRoomDisconnection from './VideoProviderHooks/useHandleRoomDisconnection';
import useHandleTrackPublicationFailed from './VideoProviderHooks/useHandleTrackPublicationFailed';
import useLocalTracks from './VideoProviderHooks/useLocalTracks';
import useRestartAudioTrackOnDeviceChange from './VideoProviderHooks/useRestartAudioTrackOnDeviceChange';
import useRoom from './VideoProviderHooks/useRoom';
import useScreenShareToggle from './VideoProviderHooks/useScreenShareToggle';
import useVideoRoomSettings from './VideoProviderHooks/useVideoRoomSettings';

export default function VideoProvider({ children, onError = () => {} }) {
  const onErrorCallback = useCallback(
    (error, body = '') => {
      if (error.message) onError(error.message, body);
      else if (typeof error === 'string') onError(error, body);
    },
    [onError],
  );
  useUserMedia();

  const { t } = useTranslation('labels');
  const [isMobileChatOpened, setMobileChatOpened] = useState(false);

  useEffect(() => {
    if (!isSupported) {
      onErrorCallback(t('browser_not_supported_label'), t('browser_not_supported_message'));
    }
  }, []);
  const { settings, setSettings } = useVideoRoomSettings();
  const connectionOptions = useVideoConnectionOptions();
  const {
    localTracks,
    getLocalVideoTrack,
    getLocalEnvironmentVideoTrack,
    getLocalAudioTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
  } = useLocalTracks(settings, onErrorCallback);
  const { room, isConnecting, isEnded, connect, disconnect, reconnect, applySettings } = useRoom(localTracks, onErrorCallback, connectionOptions, getAudioAndVideoTracks);
  const [isSharingScreen, toggleScreenShare] = useScreenShareToggle(room, onError);
  useHandleRoomDisconnection(room, onError, removeLocalAudioTrack, removeLocalVideoTrack, isSharingScreen, toggleScreenShare);
  useHandleTrackPublicationFailed(room, onError);
  useRestartAudioTrackOnDeviceChange(localTracks);

  const [isBackgroundSelectionOpen, setIsBackgroundSelectionOpen] = useState(false);
  return (
    <VideoContext.Provider
      /* eslint-disable react/jsx-no-constructed-context-values */
      value={{
        room,
        localTracks,
        isConnecting,
        isEnded,
        onError: onErrorCallback,
        getLocalVideoTrack,
        getLocalEnvironmentVideoTrack,
        getLocalAudioTrack,
        connect,
        disconnect,
        reconnect,
        isAcquiringLocalTracks,
        removeLocalVideoTrack,
        isSharingScreen,
        toggleScreenShare,
        getAudioAndVideoTracks,
        isBackgroundSelectionOpen,
        setIsBackgroundSelectionOpen,
        settings,
        setSettings,
        applySettings,
        isMobileChatOpened,
        setMobileChatOpened,
      }}
    >
      {children}
    </VideoContext.Provider>
  );
}

VideoProvider.propTypes = {
  children: PropTypes.element.isRequired,
  onError: PropTypes.func.isRequired,
};
